import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { field, label } from "./shared";

const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Typing a vehicle number in the account search picks its account.
const useVehicleAccountLink = (open, acctOpen, acctQuery, accounts, pick) => {
  useEffect(() => {
    if (!open || !acctOpen) return;
    const query = (acctQuery || "").trim();
    if (!query || accounts.some((a) => a.account.toLowerCase().includes(query.toLowerCase()))) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/parties/by-vehicle", { params: { vehicle: query } });
        if (data && data.account_name) { pick(data.account_name); toast.success(`Linked account "${data.account_name}" from vehicle`); }
      } catch (e) { console.debug(e); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open, acctQuery, acctOpen, accounts]);
};

const AccountList = ({ accounts, query, matches, onCreate, onPick }) => (
  <div data-testid="receiptdoc-account-list" className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-md border border-input bg-card shadow-lg">
    {query.trim() && !accounts.some((a) => a.account.toLowerCase() === query.trim().toLowerCase()) && (
      <button type="button" data-testid="receiptdoc-account-create" onMouseDown={(e) => e.preventDefault()} onClick={() => onCreate(query)}
        className="w-full text-left px-3 py-2 text-sm bg-primary/10 text-primary hover:bg-primary/20 flex items-center gap-2 border-b border-border font-medium">
        <Plus size={14} className="shrink-0" /> Create new account "{query.trim()}"
      </button>
    )}
    {matches.slice(0, 50).map((a) => (
      <button key={a.account} type="button" data-testid={`receiptdoc-account-opt-${a.account}`} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(a.account)}
        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex justify-between gap-2">
        <span className="truncate">{a.account}</span>
        <span className="num text-muted-foreground shrink-0">{money(a.outstanding)}</span>
      </button>
    ))}
    {matches.length === 0 && !query.trim() && <div className="px-3 py-2 text-xs text-muted-foreground">{accounts.length === 0 ? "No credit accounts yet. Type a name to create one." : "Type to search, or add a new account."}</div>}
  </div>
);

export const CreditAccountPicker = ({ open, form, setForm, accounts, setAccounts }) => {
  const [query, setQuery] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const pick = (acc) => { setForm((f) => ({ ...f, account_name: acc, received_from: acc })); setQuery(acc); setListOpen(false); };
  useVehicleAccountLink(open, listOpen, query, accounts, pick);

  const create = async (name) => {
    const n = (name || "").trim();
    if (!n) return;
    try { await api.post("/parties", { name: n, account_name: n, is_credit_account: true }); } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "";
      if (!/exist/i.test(msg)) { toast.error(msg); return; }
    }
    try { setAccounts((await api.get("/credit-accounts", { params: { include_all: true } })).data || []); } catch (e) { console.error(e); }
    pick(n);
    toast.success(`Account "${n}" selected`);
  };

  const selected = accounts.find((a) => a.account === form.account_name);
  const matches = accounts.filter((a) => a.account.toLowerCase().includes((query || "").toLowerCase()));
  return (
    <div>
      <label className={label}>Credit account <span className="text-destructive">*</span></label>
      <div className="relative">
        <input data-testid="receiptdoc-account-search" className={field} autoComplete="off" placeholder="Type to search credit account…"
          value={listOpen ? query : form.account_name}
          onFocus={() => { setQuery(form.account_name || ""); setListOpen(true); }}
          onBlur={() => setTimeout(() => setListOpen(false), 150)}
          onChange={(e) => { setQuery(e.target.value); setListOpen(true); setForm({ ...form, account_name: "", received_from: "" }); }} />
        {listOpen && <AccountList accounts={accounts} query={query} matches={matches} onCreate={create} onPick={pick} />}
      </div>
      {selected && (
        <div className="mt-2 rounded-md bg-muted p-3 text-sm flex justify-between">
          <span className="text-muted-foreground">Account outstanding</span>
          <span className="num font-bold text-destructive">{money(selected.outstanding)}</span>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-1">This payment reduces the account's outstanding; any surplus is kept as an advance. Type a new name and tap "Create new account" to record an advance for a new party.</p>
    </div>
  );
};
